import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import '../assets/styles/Timeline.scss';

const workCardStyle = { background: '#0C1426', color: '#F0F6FC', border: '1px solid #1E293B', boxShadow: 'none' };
const workArrowStyle = { borderRight: '7px solid #1E293B' };
const workIconStyle = { background: '#FF9900', color: '#080D1A' };

const eduCardStyle = { background: '#0C1426', color: '#F0F6FC', border: '1px solid #1E293B', boxShadow: 'none' };
const eduArrowStyle = { borderRight: '7px solid #1E293B' };
const eduIconStyle = { background: '#3FB950', color: '#080D1A' };

function Timeline() {
  return (
    <div className="container" id="history">
      <div className="timeline-container">
        <div className="timeline-header">
          <span className="timeline-tag">{'// Experience'}</span>
          <h1>Career & Education</h1>
        </div>

        <VerticalTimeline lineColor="#1E293B">

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={workCardStyle}
            contentArrowStyle={workArrowStyle}
            date="Oct 2024 – Present"
            iconStyle={workIconStyle}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title">Cloud Infrastructure Engineer</h3>
            <h4 className="vertical-timeline-element-subtitle">Emnify - Packet Gateway — Berlin, Germany</h4>
            <p>
              Led the migration of the company's primary control-plane architecture to Kubernetes on AWS EKS,
              enabling declarative lifecycle management and significantly reducing infrastructure operational overhead
              across the Packet Gateway stack. Led the adoption of distributed tracing using OpenTelemetry,
              instrumenting both control-plane and data-plane components for end-to-end observability. Contributed
              to two Go projects in the Kubernetes networking stack: a WireGuard peer and routing manager, and a
              custom CNI plugin for pod ENI lifecycle management resolving race conditions in high-churn AWS EKS
              environments. Built and maintained CI/CD pipelines using GitHub Actions and Jenkins, with ArgoCD
              handling GitOps-based continuous deployment of Kubernetes workloads.
            </p>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--education"
            contentStyle={eduCardStyle}
            contentArrowStyle={eduArrowStyle}
            date="Sep 2023 – Sep 2024"
            iconStyle={eduIconStyle}
            icon={<FontAwesomeIcon icon={faGraduationCap} />}
          >
            <h3 className="vertical-timeline-element-title">M.Sc. Software Engineering</h3>
            <h4 className="vertical-timeline-element-subtitle">University of Europe for Applied Sciences — Berlin, Germany</h4>
            <p>
              Thesis: "Accelerating Development Cycle for Cloud Containerized Applications: A GitOps Approach"
            </p>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={workCardStyle}
            contentArrowStyle={workArrowStyle}
            date="Jan 2023 – Sep 2024"
            iconStyle={workIconStyle}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title">DevOps Engineer</h3>
            <h4 className="vertical-timeline-element-subtitle">FOO Mobi — Beirut, Lebanon</h4>
            <p>
              Deployed and operated a mission-critical fintech platform on AWS achieving 99.9%+ uptime through
              multi-AZ architecture and Kubernetes workload orchestration on EKS, managing deployments, autoscaling,
              and namespace-level resource isolation. Accelerated release cycles by implementing GitOps pipelines
              with ArgoCD and GitLab CI/CD, eliminating manual intervention from production rollouts. Achieved
              PCI-DSS compliance across all payment workloads and reduced cloud spend by 40% through FinOps
              analysis and right-sizing. Managed all environments as code with Terraform and Ansible, and set up
              monitoring and alerting with Prometheus and Grafana across all environments.
            </p>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={workCardStyle}
            contentArrowStyle={workArrowStyle}
            date="Sep 2019 – Jan 2023"
            iconStyle={workIconStyle}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title">Cloud Solutions Architect</h3>
            <h4 className="vertical-timeline-element-subtitle">Securealm — Beirut, Lebanon</h4>
            <p>
              Architected cloud-native microservices platforms on AWS and GCP for multiple enterprise clients,
              delivering high-availability systems with automated failover and horizontal scaling. Designed
              FreeRADIUS-based AAA services with custom scripting hooks and VPN infrastructure (OpenVPN, IKEv2,
              WireGuard) supporting secure SaaS access for enterprise clients. Designed and implemented automation
              tooling in Bash, Python, and PERL, and event-driven pipelines with Kafka and AWS SQS, eliminating
              repetitive operational tasks. Implemented GitOps-driven deployment automation with AWS CodePipeline
              for DNS server and infrastructure rollouts across a multi-region AWS setup.
            </p>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={workCardStyle}
            contentArrowStyle={workArrowStyle}
            date="Sep 2018 – Sep 2019"
            iconStyle={workIconStyle}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title">Systems Engineer – Technical Support Lead</h3>
            <h4 className="vertical-timeline-element-subtitle">Securealm — Beirut, Lebanon</h4>
            <p>
              Led the technical support team serving distributed regional clients, establishing escalation workflows
              and operational runbooks that improved resolution consistency across VPN, DNS, and production
              infrastructure. Mentored support engineers through structured training programs and performance
              reviews, raising team capability and adherence to operational standards. Administered Linux-based
              server infrastructure and network services (VPN, DNS, firewalls) while standardizing support
              processes and compliance workflows, establishing measurable service quality benchmarks.
            </p>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--education"
            contentStyle={eduCardStyle}
            contentArrowStyle={eduArrowStyle}
            date="Sep 2021 – Sep 2022"
            iconStyle={eduIconStyle}
            icon={<FontAwesomeIcon icon={faGraduationCap} />}
          >
            <h3 className="vertical-timeline-element-title">M.Sc. Management Information Systems</h3>
            <h4 className="vertical-timeline-element-subtitle">Lebanese University — Beirut, Lebanon</h4>
            <p>
              Project: "Analytics Pipelines using AWS Kinesis Firehose"
            </p>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--education"
            contentStyle={eduCardStyle}
            contentArrowStyle={eduArrowStyle}
            date="Sep 2014 – Sep 2018"
            iconStyle={eduIconStyle}
            icon={<FontAwesomeIcon icon={faGraduationCap} />}
          >
            <h3 className="vertical-timeline-element-title">B.Sc. Management Information Systems</h3>
            <h4 className="vertical-timeline-element-subtitle">Lebanese University — Beirut, Lebanon</h4>
            <p>
              Project: "Restaurant Online Ordering Platform"
            </p>
          </VerticalTimelineElement>

        </VerticalTimeline>
      </div>
    </div>
  );
}

export default Timeline;
